/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Contexto para gestión de verificación OTP
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import api from "../axiosConfig";
import { esAdministrador } from "../utils/perfil";
import { useUser } from "../UserContext";

const OtpContext = createContext();

export const useOtp = () => {
  const context = useContext(OtpContext);
  if (!context) {
    throw new Error("useOtp debe ser usado dentro de OtpProvider");
  }
  return context;
};

export const OtpProvider = ({ children }) => {
  const { user } = useUser();
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpConfig, setOtpConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar configuración OTP al iniciar
  const fetchOtpConfig = useCallback(async () => {
    // El administrador de vehículos no inicia ni finaliza traslados
    if (!user || esAdministrador(user)) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/conductor/otp/configuracion");
      const config = response.data?.data || response.data;

      setOtpEnabled(config?.otp_habilitado || config?.habilitado || false);
      setOtpConfig(config);
    } catch (error) {
      console.error("Error cargando configuración OTP:", error);
      // Si hay error, asumir que OTP no está habilitado
      setOtpEnabled(false);
      setOtpConfig(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOtpConfig();
  }, [fetchOtpConfig]);

  /**
   * Verificar si un traslado requiere OTP
   * @param {Object} traslado - El traslado a verificar
   * @param {string} tipo - 'inicio' o 'finalizacion'
   * @returns {boolean}
   */
  const requiresOtp = useCallback((traslado, tipo) => {
    if (!otpEnabled) return false;

    // Verificar configuración específica del traslado
    if (traslado?.otp_requerido === false) return false;

    // Verificar si ya fue verificado
    if (tipo === 'inicio' && traslado?.otp_inicio_verificado) return false;
    if (tipo === 'finalizacion' && traslado?.otp_fin_verificado) return false;

    return true;
  }, [otpEnabled]);

  /**
   * Solicitar código OTP para inicio
   */
  const solicitarOtpInicio = useCallback(async (trasladoId) => {
    try {
      const response = await api.post(`/conductor/otp/solicitar-inicio/${trasladoId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error solicitando OTP inicio:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Error al solicitar código"
      };
    }
  }, []);

  /**
   * Verificar código OTP para inicio
   */
  const verificarOtpInicio = useCallback(async (trasladoId, codigo) => {
    try {
      const response = await api.post(`/conductor/otp/verificar-inicio/${trasladoId}`, { codigo });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error verificando OTP inicio:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Código incorrecto"
      };
    }
  }, []);

  /**
   * Solicitar código OTP para finalización
   */
  const solicitarOtpFinalizacion = useCallback(async (trasladoId) => {
    try {
      const response = await api.post(`/conductor/otp/solicitar-finalizacion/${trasladoId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error solicitando OTP finalización:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Error al solicitar código"
      };
    }
  }, []);

  /**
   * Verificar código OTP para finalización
   */
  const verificarOtpFinalizacion = useCallback(async (trasladoId, codigo) => {
    try {
      const response = await api.post(`/conductor/otp/verificar-finalizacion/${trasladoId}`, { codigo });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error verificando OTP finalización:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Código incorrecto"
      };
    }
  }, []);

  /**
   * Reenviar código OTP
   */
  const reenviarOtp = useCallback(async (trasladoId) => {
    try {
      const response = await api.post(`/conductor/otp/reenviar/${trasladoId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error reenviando OTP:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Error al reenviar código"
      };
    }
  }, []);

  const value = {
    otpEnabled,
    otpConfig,
    loading,
    requiresOtp,
    solicitarOtpInicio,
    verificarOtpInicio,
    solicitarOtpFinalizacion,
    verificarOtpFinalizacion,
    reenviarOtp,
    refreshConfig: fetchOtpConfig,
  };

  return (
    <OtpContext.Provider value={value}>
      {children}
    </OtpContext.Provider>
  );
};

OtpProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OtpContext;
